import type { Control } from "./Control.js";
import type { Rect } from "../../common/geometry.js";
import type { RowRange } from "./virtualWindow.js";
import type { TooltipContent } from "../render/RenderContext.js";

/**
 * What a screen reader is told about a control.
 *
 * Nothing Webwin draws is reachable by one: an assistive technology reads the
 * accessibility tree the browser builds from the DOM, and a canvas is a single
 * node with no inside. So a desktop that opts in grows a parallel tree of
 * hidden elements, one per control, and this module is the part that decides
 * what each of them says.
 *
 * Pure on purpose - no DOM, no controls imported, nothing that needs a GPU.
 * `Desktop` declares `DesktopWindow` structurally rather than importing the
 * `Window` control precisely so core does not depend on `src/controls`, and a
 * module here that reached the other way would drag every control into the
 * core bundle.
 */
export type AccessibleRole =
  | "button"
  | "checkbox"
  | "radio"
  | "textbox"
  | "link"
  | "listbox"
  | "option"
  | "grid"
  | "row"
  | "gridcell"
  | "columnheader"
  | "tree"
  | "treeitem"
  | "menubar"
  | "menu"
  | "menuitem"
  | "menuitemcheckbox"
  | "menuitemradio"
  | "toolbar"
  | "tablist"
  | "tab"
  | "tabpanel"
  | "combobox"
  | "slider"
  | "spinbutton"
  | "progressbar"
  | "dialog"
  | "group"
  | "region"
  | "status"
  | "separator"
  | "img"
  /** Contributes no element of its own: a layout container, or plain words. */
  | "none";

/**
 * One node of the accessibility tree.
 *
 * **Every index here is 0-based**, like the rest of the framework. ARIA wants
 * them 1-based, and `ariaAttributesFor` is the one place that adds the one -
 * because a describer that forgets is a reader announcing "item 2 of 100" for
 * the third row, and that is invisible in a screenshot.
 */
export interface AccessibleNode {
  role: AccessibleRole;
  /** What it is called. Empty means "nothing has named this yet". */
  name: string;
  /** Extra prose, announced after the name. */
  description?: string;
  /** Spoken in place of a bare number, for a value that is really a word. */
  valueText?: string;
  valueNow?: number;
  valueMin?: number;
  valueMax?: number;
  checked?: boolean | "mixed";
  expanded?: boolean;
  selected?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  multiSelectable?: boolean;
  orientation?: "horizontal" | "vertical";
  /** 0 for a root node. */
  level?: number;
  /** 0-based position among siblings, not among flattened rows. */
  posInSet?: number;
  /** A count, so it is passed through untouched. */
  setSize?: number;
  /** 0-based. A grid's header row is 0 and its first body row is 1. */
  rowIndex?: number;
  colIndex?: number;
  /** Counts. A grid's `rowCount` includes its header row. */
  rowCount?: number;
  colCount?: number;
  sort?: "ascending" | "descending" | "none";
  /** "Alt+F", from a mnemonic or an accelerator. */
  keyShortcuts?: string;
  live?: "polite" | "assertive";
  /**
   * Words to put in the element as a text node, for find-in-page.
   *
   * Distinct from `name`: a button is named "Save" and wants no text node,
   * because `aria-label` already says so and the words would then be found
   * twice by one Ctrl+F. A label is nothing but its words.
   */
  text?: string;
}

/** Turns a control into a node, or into nothing at all. */
export type AccessibleDescriber = (control: Control) => AccessibleNode | null;

const describers = new Map<Function, AccessibleDescriber>();
const itemSources = new Map<Function, AccessibleItemSource>();

/**
 * The nearest entry for a class or any class it descends from.
 *
 * Walking the prototype chain is what makes `class GlyphButton extends Button`
 * work without registering it: a subclass that changes how a control looks has
 * not usually changed what it is.
 */
function lookup<T>(table: Map<Function, T>, ctor: unknown): T | null {
  for (let c = ctor; typeof c === "function"; c = Object.getPrototypeOf(c) as unknown) {
    const found = table.get(c);
    if (found) return found;
  }
  return null;
}

/**
 * Teach the accessibility layer about a control class.
 *
 * Keyed on the constructor rather than on a name, which is a departure from
 * `registerFlare`, `registerGlyph` and `registerElement`. Those are
 * configuration surfaces where the key is a string somebody writes in markup
 * or a theme. A describer is internal dispatch on a type, and a string key
 * would be a join that goes stale in silence the first time a class is
 * renamed.
 */
export function registerDescriber(ctor: Function, describe: AccessibleDescriber): void {
  describers.set(ctor, describe);
}

/** The describer for a class, or for the nearest ancestor that has one. */
export function describerFor(ctor: unknown): AccessibleDescriber | null {
  return lookup(describers, ctor);
}

/**
 * Rows a control draws itself, rather than child controls it contains.
 *
 * A list, a grid, a tree and a menu all hold their contents as plain data and
 * paint only the part on screen. Mirroring every row would undo that - a
 * hundred thousand options is a hundred thousand elements, on a frame budget
 * of sixteen milliseconds - so the source reports the whole `count` and only
 * materialises `range`. `aria-setsize` and `aria-posinset` exist for exactly
 * this, and let a reader say "3 of 100000" truthfully from thirty elements.
 */
export interface AccessibleItems {
  /** Every row there is, not every row materialised. */
  count: number;
  /** The rows this snapshot will build, inclusive. */
  range: RowRange;
  /** Describe one row by its absolute index. */
  item(index: number): AccessibleNode | null;
  /**
   * Where a row sits on the canvas, if the control can say.
   *
   * Worth supplying: it is what lets a phone read the row under a finger,
   * which is how TalkBack is mostly used and does not involve focus at all.
   */
  itemBounds?(index: number): Rect | null;
}

export type AccessibleItemSource = (control: Control) => AccessibleItems | null;

/** Teach the accessibility layer that a control class draws its own rows. */
export function registerItemSource(ctor: Function, source: AccessibleItemSource): void {
  itemSources.set(ctor, source);
}

/** The item source for a class, or for the nearest ancestor that has one. */
export function itemSourceFor(ctor: unknown): AccessibleItemSource | null {
  return lookup(itemSources, ctor);
}

/** Drop every registration. Exists so one test cannot leak into the next. */
export function clearDescribers(): void {
  describers.clear();
  itemSources.clear();
}

/**
 * The DOM id for a mirror node.
 *
 * One function, because `aria-activedescendant` names an element by id and if
 * the two spellings ever disagree the attribute is not rejected - it is
 * ignored, and focus simply stops being announced.
 */
export function mirrorId(key: string): string {
  return `webwin-a11y-${key}`;
}

// --- reading a control's shape -------------------------------------------

interface Shaped {
  text?: unknown;
  checked?: unknown;
  indeterminate?: unknown;
  group?: unknown;
  readOnly?: unknown;
  value?: unknown;
  minimum?: unknown;
  maximum?: unknown;
  step?: unknown;
  orientation?: unknown;
  increment?: unknown;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const has = (c: object, key: string): boolean => key in c;

/** The words of a tooltip, however it was written. */
export function tooltipText(tip: TooltipContent | null): string {
  if (!tip) return "";
  if (typeof tip === "string") return tip.trim();
  return tip
    .map((s) => s.text)
    .join("")
    .trim();
}

/**
 * Describe a control from the shape it already has.
 *
 * The fallback, and it carries most of the library: a control with `text` and
 * a tab stop is a button, one that consumes typed characters is a text box,
 * one with a value between a minimum and a maximum is a slider. None of those
 * controls had to be told, which is why this did not arrive as forty diffs
 * putting ARIA vocabulary into `src/controls`.
 *
 * Returns null for a control with nothing to say - a `Panel`, a `Grid`, a
 * `StackPanel`. That is not a gap. A reader walking to a button should not
 * first hear "group, group, group", and an element per layout container would
 * multiply the size of the mirror to say nothing at all.
 */
export function describeByShape(control: Control): AccessibleNode | null {
  const c = control as Control & Shaped;
  const tip = tooltipText(control.tooltip);
  const disabled = !control.effectivelyEnabled || control.inputBlocked;
  const flag = disabled ? { disabled: true } : {};

  if (control.acceptsTextInput) {
    return {
      role: "textbox",
      // Named by its tooltip, never by its contents. A field named with what
      // it holds has a reader say the whole value before every keystroke echo.
      name: tip,
      valueText: str(c.text),
      ...(c.readOnly === true ? { readOnly: true } : {}),
      ...flag,
    };
  }

  if (has(c, "checked")) {
    return {
      role: has(c, "group") ? "radio" : "checkbox",
      name: str(c.text).trim() || tip,
      checked: c.indeterminate === true ? "mixed" : c.checked === true,
      ...flag,
    };
  }

  const value = num(c.value);
  const min = num(c.minimum);
  const max = num(c.maximum);
  if (value !== null && min !== null && max !== null) {
    // Tested for a *number* rather than for the key. `ProgressBar.step()` is
    // a method that advances the bar, and a presence check called it a
    // slider - which a reader then invites the user to drag.
    const role: AccessibleRole =
      num(c.step) !== null ? "slider" : num(c.increment) !== null ? "spinbutton" : "progressbar";
    return {
      role,
      name: str(c.text).trim() || tip,
      valueNow: value,
      valueMin: min,
      valueMax: max,
      ...(c.orientation === "vertical" || c.orientation === "horizontal"
        ? { orientation: c.orientation }
        : {}),
      ...flag,
    };
  }

  if (has(c, "text")) {
    const text = str(c.text);
    // `tabStop` and not `focusable`: the latter folds in enabled, visible and
    // blocked, so a grayed-out button stopped being a button and described
    // itself as a stray caption - losing both its role and the very state
    // that was worth announcing.
    if (control.tabStop) return { role: "button", name: text.trim() || tip, ...flag };
    // Not focusable and made of words: a caption. No role, because ARIA has
    // none for plain text - the words are the whole contribution, and they are
    // what Ctrl+F needs to find.
    if (text.trim()) return { role: "none", name: "", text };
    return tip ? { role: "img", name: tip, ...flag } : null;
  }

  return null;
}

// --- turning a node into attributes --------------------------------------

/**
 * The one table mapping a node onto DOM attributes.
 *
 * Two rules worth stating, because both fail quietly.
 *
 * Indices arrive 0-based and leave 1-based. Every describer counts the way the
 * framework counts and this adds the one, so there is a single line to get
 * wrong rather than a dozen.
 *
 * A state that constitutes a role is always written, even when false.
 * `aria-checked="false"` is a box nobody has ticked; an *absent* `aria-checked`
 * is not a checkbox at all, and a reader announces it as a button.
 */
export function ariaAttributesFor(node: AccessibleNode): Record<string, string> {
  const a: Record<string, string> = {};
  if (node.role !== "none") a["role"] = node.role;
  // A node carrying its own words is named by them. A second aria-label would
  // have the browser find the word twice and a reader say it twice.
  if (node.name && !node.text) a["aria-label"] = node.name;
  if (node.description) a["aria-description"] = node.description;

  if (node.checked !== undefined) a["aria-checked"] = String(node.checked);
  if (node.expanded !== undefined) a["aria-expanded"] = String(node.expanded);
  if (node.selected !== undefined) a["aria-selected"] = String(node.selected);
  if (node.disabled) a["aria-disabled"] = "true";
  if (node.readOnly) a["aria-readonly"] = "true";
  if (node.multiSelectable) a["aria-multiselectable"] = "true";
  if (node.orientation) a["aria-orientation"] = node.orientation;

  if (node.valueNow !== undefined) a["aria-valuenow"] = String(node.valueNow);
  if (node.valueMin !== undefined) a["aria-valuemin"] = String(node.valueMin);
  if (node.valueMax !== undefined) a["aria-valuemax"] = String(node.valueMax);
  if (node.valueText) a["aria-valuetext"] = node.valueText;

  if (node.level !== undefined) a["aria-level"] = String(node.level + 1);
  if (node.posInSet !== undefined) a["aria-posinset"] = String(node.posInSet + 1);
  if (node.setSize !== undefined) a["aria-setsize"] = String(node.setSize);
  if (node.rowIndex !== undefined) a["aria-rowindex"] = String(node.rowIndex + 1);
  if (node.colIndex !== undefined) a["aria-colindex"] = String(node.colIndex + 1);
  if (node.rowCount !== undefined) a["aria-rowcount"] = String(node.rowCount);
  if (node.colCount !== undefined) a["aria-colcount"] = String(node.colCount);
  if (node.sort) a["aria-sort"] = node.sort;

  if (node.keyShortcuts) a["aria-keyshortcuts"] = node.keyShortcuts;
  if (node.live) a["aria-live"] = node.live;
  return a;
}
